import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SyncStats {
  total_processed: number;
  success_count: number;
  error_count: number;
  messages_imported?: number;
}

interface SyncResult {
  success: boolean;
  message?: string;
  error?: string;
  stats?: SyncStats;
  pipeline_statuses?: Record<number, string>;
  contact_id?: string;
  case_id?: string;
  messages_imported?: number;
  kommo_lead_id?: number;
}

export function useKommoSync() {
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);
  const [syncProgress, setSyncProgress] = useState<string | null>(null);

  // Test connection to Kommo API
  const testConnection = useCallback(async (): Promise<SyncResult> => {
    setIsTesting(true);
    setLastResult(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('kommo-sync', {
        body: { action: 'test' },
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      const result: SyncResult = data;
      setLastResult(result);
      
      if (result.success) {
        toast.success('Conexão com Kommo estabelecida com sucesso!');
      } else {
        toast.error(result.error || 'Falha na conexão');
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      const result: SyncResult = { success: false, error: errorMessage };
      setLastResult(result);
      toast.error(`Erro ao testar conexão: ${errorMessage}`);
      return result;
    } finally {
      setIsTesting(false);
    }
  }, []);

  // Full sync from Kommo
  const syncAll = useCallback(async (): Promise<SyncResult> => {
    setIsSyncing(true);
    setLastResult(null);
    setSyncProgress('Iniciando sincronização...');
    
    try {
      toast.info('Sincronização iniciada. Isso pode levar alguns minutos...');
      
      const { data, error } = await supabase.functions.invoke('kommo-sync', {
        body: { action: 'sync' },
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      const result: SyncResult = data;
      setLastResult(result);
      setSyncProgress(null);
      
      if (result.success && result.stats) {
        toast.success(
          `Sincronização concluída: ${result.stats.success_count} sucesso, ${result.stats.error_count} erros`
        );
      } else {
        toast.error(result.error || 'Falha na sincronização');
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      const result: SyncResult = { success: false, error: errorMessage };
      setLastResult(result);
      setSyncProgress(null);
      toast.error(`Erro na sincronização: ${errorMessage}`);
      return result;
    } finally {
      setIsSyncing(false);
    }
  }, []);

  // Sync a single lead
  const syncSingleLead = useCallback(async (leadId: string | number): Promise<SyncResult> => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('kommo-sync', {
        body: { action: 'sync_single', lead_id: leadId },
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      const result: SyncResult = data;
      
      if (result.success) {
        toast.success('Lead sincronizado com sucesso!');
      } else {
        toast.error(result.error || 'Falha ao sincronizar lead');
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao sincronizar lead: ${errorMessage}`);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Sync messages only for an existing contact
  const syncContactMessages = useCallback(async (contactId: string, leadId?: string | number): Promise<SyncResult> => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('kommo-sync', {
        body: { action: 'sync_messages', contact_id: contactId, lead_id: leadId },
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      const result: SyncResult = data;
      
      if (result.success) {
        toast.success(`${result.messages_imported || 0} mensagens importadas do Kommo!`);
      } else {
        toast.error(result.error || 'Falha ao importar mensagens');
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao importar mensagens: ${errorMessage}`);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get sync history from kommo_import_rows
  const getSyncHistory = useCallback(async (limit = 100) => {
    const { data, error } = await supabase
      .from('kommo_import_rows')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Error fetching sync history:', error);
      return [];
    }
    
    return data || [];
  }, []);

  return {
    // State
    isLoading,
    isTesting,
    isSyncing,
    lastResult,
    syncProgress,
    
    // Actions
    testConnection,
    syncAll,
    syncSingleLead,
    syncContactMessages,
    getSyncHistory,
  };
}
